import { formatDate as sharedFormatDate } from '@/utils';
/**
 * The customer portal's Grievances section (board #393, phase 1).
 *
 * A customer raises a grievance against one of their policies, gets a GRV-
 * ticket number and a resolution due date (the TAT clock), sees the insurer's
 * Grievance Officer (L1) and Nodal / Appellate officer (L2), and can escalate
 * one level at a time. L3 is IRDAI Bima Bharosa -- shown as a reference and a
 * link only; nothing is sent to IRDAI.
 *
 * Scoping is the server's: `/communications/tickets/` returns only this
 * customer's tickets and the policy picker refuses anyone else's policy.
 */

import { ExclamationTriangleIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import toast from 'react-hot-toast';

import {
  Badge,
  Button,
  Card,
  CardHeader,
  ContentLoader,
  EmptyState,
  Modal,
  ModalFooter,
} from '@/components/common';
import { PageHeader } from '@/components/layout';
import { getErrorMessage } from '@/services/api';
import { grievancesApi, type Grievance } from '@/services/grievances';

const LIST_KEY = ['grievances', 'list'] as const;

const inputClass =
  'mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:bg-gray-800 dark:border-gray-600';

function formatDate(value: string | null): string {
  return sharedFormatDate(value) || '-';
}

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function RaiseGrievanceForm({ onRaised }: { onRaised: (grievance: Grievance) => void }) {
  const policies = useQuery({
    queryKey: ['grievances', 'policies'],
    queryFn: () => grievancesApi.policies(),
  });
  const categories = useQuery({
    queryKey: ['grievances', 'categories'],
    queryFn: () => grievancesApi.categories(),
  });
  const [policy, setPolicy] = useState('');
  const [category, setCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');

  const raise = useMutation({
    mutationFn: () =>
      grievancesApi.create({ policy, support_category: category, subject, description }),
    onSuccess: (grievance) => {
      toast.success(`Grievance ${grievance.ticket_number} raised`);
      onRaised(grievance);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const ready = policy && category && subject.trim() && description.trim();

  return (
    <Card>
      <CardHeader
        title="Raise a grievance"
        subtitle="Tell us which policy it is about and what went wrong"
      />
      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) raise.mutate();
        }}
      >
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Policy
          <select className={inputClass} value={policy} onChange={(e) => setPolicy(e.target.value)}>
            <option value="">{policies.isLoading ? 'Loading...' : 'Select a policy'}</option>
            {(policies.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.policy_number}
                {p.insurer_name ? ` - ${p.insurer_name}` : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Category
          <select
            className={inputClass}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">{categories.isLoading ? 'Loading...' : 'Select a category'}</option>
            {(categories.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 sm:col-span-2">
          Subject
          <input
            className={inputClass}
            value={subject}
            maxLength={300}
            onChange={(e) => setSubject(e.target.value)}
          />
        </label>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 sm:col-span-2">
          Details
          <textarea
            className={inputClass}
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        {(policies.isError || categories.isError) && (
          <p className="text-sm text-red-600 sm:col-span-2">
            Could not load your policies or the categories.
          </p>
        )}
        <div className="sm:col-span-2">
          <Button type="submit" disabled={!ready || raise.isPending} isLoading={raise.isPending}>
            Submit grievance
          </Button>
        </div>
      </form>
    </Card>
  );
}

function GrievanceDetail({ id }: { id: string }) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ['grievances', 'detail', id],
    queryFn: () => grievancesApi.get(id),
  });
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  // Set synchronously on the first click: state alone lags a render behind a
  // double click, and the dialog stays clickable until the response lands.
  const sent = useRef(false);
  const escalate = useMutation({
    mutationFn: () => grievancesApi.escalate(id, reason.trim()),
    onSuccess: (data) => {
      setConfirming(false);
      client.setQueryData(['grievances', 'detail', id], data);
      client.invalidateQueries({ queryKey: LIST_KEY });
      setReason('');
      toast.success(`Escalated to ${data.escalation_level_display ?? 'the next level'}`);
    },
    onError: (error) => {
      sent.current = false;
      setConfirming(false);
      toast.error(getErrorMessage(error));
    },
  });
  const confirmEscalation = () => {
    if (sent.current) return;
    sent.current = true;
    escalate.mutate();
  };

  if (query.isLoading) return <ContentLoader />;
  if (query.isError || !query.data) {
    return <p className="text-sm text-red-600">Could not load this grievance.</p>;
  }
  const g = query.data;
  const canEscalate = (g.escalation_level ?? 1) < 3 && g.status !== 'closed';

  return (
    <Card>
      <CardHeader
        title={`${g.ticket_number} - ${g.subject}`}
        subtitle={g.insurer_name ?? undefined}
      />
      <dl className="grid gap-3 text-sm sm:grid-cols-4">
        <div>
          <dt className="text-gray-500">Status</dt>
          <dd className="font-medium">{statusLabel(g.status)}</dd>
        </div>
        <div>
          <dt className="text-gray-500">Policy</dt>
          <dd className="font-medium">{g.policy_number ?? '-'}</dd>
        </div>
        <div>
          <dt className="text-gray-500">Resolve by</dt>
          <dd className="font-medium">
            {formatDate(g.sla_due_at)}{' '}
            {g.is_tat_breached && (
              <Badge variant="error" size="sm">
                Overdue
              </Badge>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500">Current level</dt>
          <dd className="font-medium">{g.escalation_level_display ?? '-'}</dd>
        </div>
      </dl>

      <h3 className="mb-2 mt-6 text-sm font-semibold text-gray-900 dark:text-gray-100">
        Escalation matrix
      </h3>
      <ol className="space-y-2">
        {(g.escalation_matrix ?? []).map((row) => (
          <li
            key={row.level}
            data-testid={`level-${row.level}`}
            className={`rounded-md border p-3 text-sm ${
              row.is_current
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200'
            }`}
          >
            <div className="flex items-center gap-2 font-medium">
              {row.label}
              {row.is_current && (
                <Badge variant="primary" size="sm">
                  Current
                </Badge>
              )}
            </div>
            {row.name || row.email || row.phone ? (
              <div className="mt-1 text-gray-600 dark:text-gray-400">
                {[row.name, row.email, row.phone].filter(Boolean).join(' | ')}
              </div>
            ) : (
              <div className="mt-1 text-gray-400">
                Contact details not yet provided by the insurer.
              </div>
            )}
            {row.note && <p className="mt-1 text-gray-600 dark:text-gray-400">{row.note}</p>}
            {row.url && (
              <a
                className="mt-1 inline-block text-primary-600 underline"
                href={row.url}
                target="_blank"
                rel="noreferrer"
              >
                {row.url}
              </a>
            )}
          </li>
        ))}
      </ol>

      {canEscalate && (
        <form
          className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            sent.current = false;
            setConfirming(true);
          }}
        >
          <label className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-300">
            Reason for escalating
            <input
              className={inputClass}
              value={reason}
              maxLength={1000}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <Button type="submit" variant="outline" disabled={escalate.isPending}>
            Escalate to next level
          </Button>
        </form>
      )}
      {canEscalate && (
        <Modal
          isOpen={confirming}
          onClose={() => !escalate.isPending && setConfirming(false)}
          title="Escalate this grievance?"
          size="sm"
        >
          <p className="text-sm text-gray-700 dark:text-gray-300">
            This moves the grievance to Level {(g.escalation_level ?? 1) + 1}.
            {(g.escalation_level ?? 1) + 1 >= 3
              ? ' Level 3 is the regulator (IRDAI Bima Bharosa). Nothing more can be escalated after this.'
              : ' You can escalate once more after this, to the regulator (IRDAI Bima Bharosa).'}
          </p>
          {reason.trim() && (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Reason: {reason.trim()}</p>
          )}
          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              disabled={escalate.isPending}
              onClick={() => setConfirming(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={escalate.isPending}
              isLoading={escalate.isPending}
              onClick={confirmEscalation}
            >
              Confirm escalation
            </Button>
          </ModalFooter>
        </Modal>
      )}

      <h3 className="mb-2 mt-6 text-sm font-semibold text-gray-900 dark:text-gray-100">
        Status history
      </h3>
      <ul className="space-y-1 text-sm">
        {(g.status_history ?? []).map((row, i) => (
          <li key={`${row.created_at}-${i}`} className="flex gap-3">
            <span className="w-28 shrink-0 text-gray-500">{formatDate(row.created_at)}</span>
            <span>{row.description}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function MyGrievancesPage() {
  const client = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const list = useQuery({ queryKey: LIST_KEY, queryFn: () => grievancesApi.list() });

  return (
    <div className="space-y-6">
      <PageHeader
        title="My Grievances"
        subtitle="Raise a grievance about a policy, track its resolution date and escalate it"
        actions={
          <Button
            leftIcon={<PlusIcon className="h-4 w-4" />}
            onClick={() => setShowForm((v) => !v)}
          >
            Raise a grievance
          </Button>
        }
      />

      {showForm && (
        <RaiseGrievanceForm
          onRaised={(grievance) => {
            setShowForm(false);
            setSelected(grievance.id);
            client.invalidateQueries({ queryKey: LIST_KEY });
          }}
        />
      )}

      <Card>
        {list.isLoading ? (
          <ContentLoader />
        ) : list.isError ? (
          <div className="text-sm text-red-600">
            Could not load your grievances.{' '}
            <button type="button" className="underline" onClick={() => list.refetch()}>
              Try again
            </button>
          </div>
        ) : !list.data?.length ? (
          <EmptyState
            icon={<ExclamationTriangleIcon className="h-8 w-8" />}
            title="No grievances"
            description="You have not raised any grievances."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-2 pr-4 font-medium">Ticket</th>
                  <th className="py-2 pr-4 font-medium">Subject</th>
                  <th className="py-2 pr-4 font-medium">Insurer</th>
                  <th className="py-2 pr-4 font-medium">Level</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Resolve by</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((g) => (
                  <tr key={g.id} className="border-t border-gray-100">
                    <td className="py-2 pr-4">
                      <button
                        type="button"
                        className="font-medium text-primary-600 underline"
                        onClick={() => setSelected(g.id)}
                      >
                        {g.ticket_number}
                      </button>
                    </td>
                    <td className="py-2 pr-4">{g.subject}</td>
                    <td className="py-2 pr-4">{g.insurer_name ?? '-'}</td>
                    <td className="py-2 pr-4">
                      {g.escalation_level ? `L${g.escalation_level}` : '-'}
                    </td>
                    <td className="py-2 pr-4">{statusLabel(g.status)}</td>
                    <td className="py-2 pr-4">
                      {formatDate(g.sla_due_at)}{' '}
                      {g.is_tat_breached && (
                        <Badge variant="error" size="sm">
                          Overdue
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {selected && <GrievanceDetail id={selected} />}
    </div>
  );
}
